// FO-323 (batch-36) — NEN 3140-aanwijzingen: gedeelde types + Zod + statusmachine.
//
// BELANGRIJK: normteksten/keuzelijsten/templates zijn DATA (beheerbaar via de
// backoffice), NOOIT hardcoded. Wat hier in code staat zijn stabiele identifiers
// (enum-waarden, niveau-ordening), de statusmachine-overgangen, en neutrale
// UI-labels.

import { z } from 'zod'
import { partialZonderDefaults } from './zod-patch'

// ─── Niveau (VOP < VP < WV < IV) ─────────────────────────────────────────────

export const Nen3140NiveauEnum = z.enum(['VOP', 'VP', 'WV', 'IV'])
export type Nen3140Niveau = z.infer<typeof Nen3140NiveauEnum>

/**
 * FO-438 B1 - DE enige tekstbron voor de niveau-omschrijving.
 *
 * De testronde bij Ojah las op de monteurkaart de VOP-omschrijving achter de
 * code VP. De omschrijving stond daar los van de code in een tweede bron (de
 * naam van het certificaattype in de database), waar het paar omgewisseld was
 * geraakt.
 *
 * Alles wat een niveau in tekst toont leidt daarom af van deze map: `LABELS`
 * hieronder plakt er de code voor, de klant-i18n spiegelt hem per taal, en de
 * drift-guard (`nen3140-niveau-label-drift.test.ts`) faalt op elke plek die een
 * omschrijving aan de verkeerde code koppelt.
 */
export const NEN3140_NIVEAU_OMSCHRIJVING: Record<Nen3140Niveau, string> = {
  VOP: 'Voldoende Onderricht Persoon',
  VP: 'Vakbekwaam Persoon',
  WV: 'Werkverantwoordelijke',
  IV: 'Installatieverantwoordelijke',
}

/** `<code> - <omschrijving>`; afgeleid, nooit los ingetypt. */
export const NEN3140_NIVEAU_LABELS: Record<Nen3140Niveau, string> = {
  VOP: `VOP - ${NEN3140_NIVEAU_OMSCHRIJVING.VOP}`,
  VP: `VP - ${NEN3140_NIVEAU_OMSCHRIJVING.VP}`,
  WV: `WV - ${NEN3140_NIVEAU_OMSCHRIJVING.WV}`,
  IV: `IV - ${NEN3140_NIVEAU_OMSCHRIJVING.IV}`,
}

/** Rangorde voor de planning-check: een hoger niveau dekt een lagere eis. */
export const NEN3140_NIVEAU_ORDE: Record<Nen3140Niveau, number> = { VOP: 1, VP: 2, WV: 3, IV: 4 }

/** Dekt `heeft` de gevraagde `vereist`? (hoger of gelijk niveau). */
export function niveauDekt(heeft: Nen3140Niveau, vereist: Nen3140Niveau): boolean {
  return NEN3140_NIVEAU_ORDE[heeft] >= NEN3140_NIVEAU_ORDE[vereist]
}

// ─── Status ──────────────────────────────────────────────────────────────────

export const NEN3140_STATUSSEN = [
  'CONCEPT',
  'WACHT_OP_CONTROLE',
  'WACHT_OP_ONDERTEKENING_MEDEWERKER',
  'WACHT_OP_ONDERTEKENING_WERKGEVER',
  'ACTIEF',
  'VERLOPEN',
  'INGETROKKEN',
  'VERVANGEN',
  'GEARCHIVEERD',
] as const
export type Nen3140Status = (typeof NEN3140_STATUSSEN)[number]

export const NEN3140_STATUS_LABELS: Record<Nen3140Status, string> = {
  CONCEPT: 'Concept',
  WACHT_OP_CONTROLE: 'Wacht op controle',
  WACHT_OP_ONDERTEKENING_MEDEWERKER: 'Wacht op handtekening medewerker',
  WACHT_OP_ONDERTEKENING_WERKGEVER: 'Wacht op handtekening werkgever',
  ACTIEF: 'Actief',
  VERLOPEN: 'Verlopen',
  INGETROKKEN: 'Ingetrokken',
  VERVANGEN: 'Vervangen',
  GEARCHIVEERD: 'Gearchiveerd',
}

/** Weergave-status (incl. de afgeleide VERLOOPT_BINNENKORT, nooit opgeslagen). */
export const NEN3140_WEERGAVE_STATUSSEN = [
  ...NEN3140_STATUSSEN,
  'VERLOOPT_BINNENKORT',
] as const
export type Nen3140WeergaveStatus = (typeof NEN3140_WEERGAVE_STATUSSEN)[number]

/**
 * Labels voor de WEERGAVE-status. DIAGNOSE batch-58 (D5): de lijst kleurde de
 * badge op `weergaveStatus` maar zette de tekst uit `NEN3140_STATUS_LABELS[status]`
 * - een aanwijzing die vandaag verloopt kreeg dus een rode badge met het woord
 * "Actief". Kleur en tekst komen nu uit dezelfde bron.
 */
export const NEN3140_WEERGAVE_STATUS_LABELS: Record<Nen3140WeergaveStatus, string> = {
  CONCEPT: 'Concept',
  WACHT_OP_CONTROLE: 'Wacht op controle',
  WACHT_OP_ONDERTEKENING_MEDEWERKER: 'Wacht op handtekening medewerker',
  WACHT_OP_ONDERTEKENING_WERKGEVER: 'Wacht op handtekening werkgever',
  ACTIEF: 'Actief',
  VERLOPEN: 'Verlopen',
  INGETROKKEN: 'Ingetrokken',
  VERVANGEN: 'Vervangen',
  GEARCHIVEERD: 'Gearchiveerd',
  VERLOOPT_BINNENKORT: 'Verloopt binnenkort',
}

/** Terminale statussen: geen verdere overgang mogelijk. */
export const NEN3140_TERMINALE_STATUSSEN: Nen3140Status[] = [
  'INGETROKKEN',
  'VERVANGEN',
  'GEARCHIVEERD',
]

/**
 * Toegestane overgangen (§5). Onmogelijke overgangen zijn geblokkeerd. De
 * directe activatie CONCEPT/WACHT_* -> ACTIEF is de fase-1-route (handmatig
 * activeren met §6-controle + scan); de service dwingt de §6-controles af.
 */
export const NEN3140_OVERGANGEN: Record<Nen3140Status, Nen3140Status[]> = {
  CONCEPT: ['WACHT_OP_CONTROLE', 'ACTIEF', 'INGETROKKEN', 'GEARCHIVEERD'],
  WACHT_OP_CONTROLE: [
    'CONCEPT',
    'WACHT_OP_ONDERTEKENING_MEDEWERKER',
    'ACTIEF',
    'INGETROKKEN',
    'GEARCHIVEERD',
  ],
  WACHT_OP_ONDERTEKENING_MEDEWERKER: [
    'WACHT_OP_ONDERTEKENING_WERKGEVER',
    'ACTIEF',
    'INGETROKKEN',
    'GEARCHIVEERD',
  ],
  WACHT_OP_ONDERTEKENING_WERKGEVER: ['ACTIEF', 'INGETROKKEN', 'GEARCHIVEERD'],
  ACTIEF: ['VERLOPEN', 'INGETROKKEN', 'VERVANGEN', 'GEARCHIVEERD'],
  VERLOPEN: ['INGETROKKEN', 'VERVANGEN', 'GEARCHIVEERD'],
  INGETROKKEN: [],
  VERVANGEN: [],
  GEARCHIVEERD: [],
}

export function isGeldigeNen3140Overgang(van: Nen3140Status, naar: Nen3140Status): boolean {
  return NEN3140_OVERGANGEN[van]?.includes(naar) ?? false
}

/** Afgeleide weergave-status uit opgeslagen status + einddatum. */
export function bepaalAanwijzingWeergaveStatus(
  status: Nen3140Status,
  einddatum: string | Date | null,
  now: Date,
  waarschuwingsDagen = 90,
): Nen3140WeergaveStatus {
  if (status !== 'ACTIEF') return status
  if (!einddatum) return 'ACTIEF'
  const eind = typeof einddatum === 'string' ? new Date(einddatum) : einddatum
  const dagen = Math.floor((eind.getTime() - now.getTime()) / 86_400_000)
  if (dagen < 0) return 'VERLOPEN'
  if (dagen <= waarschuwingsDagen) return 'VERLOOPT_BINNENKORT'
  return 'ACTIEF'
}

// ─── Overige enums ───────────────────────────────────────────────────────────

export const Nen3140GeldigheidsBronEnum = z.enum([
  'GELIJK_AAN_CERTIFICAAT',
  'EEN_JAAR',
  'DRIE_JAAR',
  'VIJF_JAAR',
  'HANDMATIG',
])
export type Nen3140GeldigheidsBron = z.infer<typeof Nen3140GeldigheidsBronEnum>

export const Nen3140VereisteActieEnum = z.enum(['WAARSCHUWEN', 'BLOKKEREN', 'OVERRIDE'])
export type Nen3140VereisteActie = z.infer<typeof Nen3140VereisteActieEnum>

export const NEN3140_VEREISTE_ACTIE_LABELS: Record<Nen3140VereisteActie, string> = {
  WAARSCHUWEN: 'Alleen waarschuwen',
  BLOKKEREN: 'Blokkeren',
  OVERRIDE: 'Blokkeren, tenzij override met reden',
}

export const Nen3140DeelGrondslagEnum = z.enum(['TOESTEMMING', 'CONTRACTUELE_NOODZAAK'])
export type Nen3140DeelGrondslag = z.infer<typeof Nen3140DeelGrondslagEnum>

export const NEN3140_DEEL_GRONDSLAG_LABELS: Record<Nen3140DeelGrondslag, string> = {
  TOESTEMMING: 'Toestemming medewerker (AVG)',
  CONTRACTUELE_NOODZAAK: 'Gedocumenteerde contractuele noodzaak',
}

/** Klant-statusbadge (nooit stil verdwijnen). */
export type Nen3140KlantBadge = 'GELDIG' | 'VERLOOPT_BINNENKORT' | 'VERLOPEN' | 'INGETROKKEN'
export const NEN3140_KLANT_BADGE_LABELS: Record<Nen3140KlantBadge, string> = {
  GELDIG: 'Geldig',
  VERLOOPT_BINNENKORT: 'Verloopt binnenkort',
  VERLOPEN: 'Verlopen',
  INGETROKKEN: 'Ingetrokken',
}

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface Nen3140StamlijstItemDTO {
  id: string
  naam: string
  categorie?: string | null
  actief: boolean
  volgorde: number
}

export interface Nen3140RolprofielDTO {
  id: string
  niveau: Nen3140Niveau
  naam: string
  omschrijving: string | null
  toegestaneWerkzaamheden: string[]
  nietToegestaneWerkzaamheden: string[]
  standaardBeperkingen: string[]
  vereisteCertificaatTypes: string[]
  vereisteSkills: string[]
  standaardGeldigheidsBron: Nen3140GeldigheidsBron
  maxGeldigheidMaanden: number | null
  vereisteGoedkeurderRol: string | null
  documentTemplateId: string | null
  // FO-334 (batch-39) — "Deel van de installatie": beheerbare keuzelijst + default.
  standaardInstallatie: string | null
  installatieOpties: string[]
  combinatieApart: boolean
  actief: boolean
  isConcept: boolean
  volgorde: number
}

export interface Nen3140AanwijzingDTO {
  id: string
  employeeId: string
  medewerkerNaam: string
  niveau: Nen3140Niveau
  rolprofielId: string | null
  status: Nen3140Status
  weergaveStatus: Nen3140WeergaveStatus
  aanwijzingsdatum: string | null
  ingangsdatum: string | null
  einddatum: string | null
  dagenTotVerloop: number | null
  werkzaamheden: string[]
  beperkingen: string[]
  omstandigheden: string | null
  installaties: string | null
  spanningsgebieden: string | null
  voorwaarden: string | null
  // FO-334 (batch-39) — resterende bijlage-D-invulvelden (autofill, overschrijfbaar).
  functie: string | null
  verantwoordelijkheden: string | null
  middelen: string[]
  plaatsAanwijzing: string | null
  certificaatId: string | null
  bewijsNotities: string | null
  afwijkingsreden: string | null
  aangewezenDoorId: string | null
  aangewezenDoorFunctie: string | null
  goedkeurderId: string | null
  goedgekeurdOp: string | null
  documentId: string | null
  documentNummer: string | null
  ondertekendMedewerkerOp: string | null
  ondertekendMedewerkerNaam: string | null
  ondertekendWerkgeverOp: string | null
  ondertekendWerkgeverNaam: string | null
  scanAanwezig: boolean
  vervangenDoorId: string | null
  ingetrokkenOp: string | null
  ingetrokkenReden: string | null
  // ── batch-58 nen3140-v2 (FO-431) ────────────────────────────────────────
  aangewezenNamensEntityId: string | null
  aangewezenNamensEntityNaam: string | null
  normversie: string | null
  opleidingNiveau: string | null
  opleidingRichting: string | null
  nenTrainingNaam: string | null
  nenTrainingDatum: string | null
  jarenErvaring: number | null
  ervaringInstallaties: string | null
  ervaringWerkzaamheden: string | null
  /** Bevroren antwoorden op de beoordelingscriteria (stap 3). */
  criteriaAntwoorden: Nen3140CriteriumAntwoordRegel[]
  criteriaNormversie: string | null
  criteriaBeoordeeldDoorId: string | null
  criteriaBeoordeeldOp: string | null
  criteriaAfwijkingMotivering: string | null
  /** Bevoegdhedenmatrix (stap 4), voorgevuld uit het rolprofiel. */
  bevoegdhedenMatrix: Nen3140MatrixRegel[]
  werkenOnderSpanning: boolean
  werkenOnderSpanningToelichting: string | null
  schakelbevoegdheid: boolean
  schakelbevoegdheidOmschrijving: string | null
  wizardStap: number
  createdAt: string
  updatedAt: string
}

export interface Nen3140VereisteDTO {
  id: string
  niveau: Nen3140Niveau
  clientId: string | null
  werkbonId: string | null
  projectId: string | null
  verplicht: boolean
  actie: Nen3140VereisteActie
  /**
   * batch-58 nen3140-v2 (FO-431 §C8) — eist deze klant naast de STREEP-
   * aanwijzing ook een eigen autorisatie van zijn installatieverantwoordelijke?
   * Zo ja, dan toetst de planning die als derde laag.
   */
  klantAutorisatieVereist: boolean
}

export interface Nen3140DelingDTO {
  id: string
  aanwijzingId: string
  clientId: string
  clientNaam: string
  grondslag: Nen3140DeelGrondslag
  grondslagToelichting: string | null
  periodeVan: string | null
  periodeTot: string | null
  automatisch: boolean
  ingetrokkenOp: string | null
  createdAt: string
}

// ─── FO-334 (batch-39) — autofill (bijlage D §2-matrix) ─────────────────────
//
// Eén server-berekening (API) levert per bijlage-D-onderdeel een VOORSTEL met
// bron-label; de wizard rekent niets zelf. Voorstellen zijn geel/overschrijfbaar
// en per veld te verversen. Niets wordt stil definitief.

export interface Nen3140AutofillVeld {
  /** Voorgestelde waarde (null = geen bron beschikbaar). */
  waarde: string | null
  /** Leesbaar bron-label voor de UI (bv. "Medewerker-dossier"). */
  bron: string
}

export type Nen3140MiddelSoort = 'PBM' | 'MEETINSTRUMENT' | 'KOFFER'

export interface Nen3140AutofillMiddelRegel {
  /** Documentregel, bv. "Multimeter Fluke 117 (serienr. F117-1, keuring geldig tot 01-03-2027)". */
  regel: string
  soort: Nen3140MiddelSoort
}

export interface Nen3140CertificaatOptieDTO {
  id: string
  label: string
  expiresAt: string | null
}

export interface Nen3140AutofillDTO {
  // Vast blok (bijlage D: naam + geboortedatum) — komt uit het dossier, niet
  // overschrijfbaar; de render leest dezelfde bron.
  medewerkerNaam: string
  geboortedatum: string | null
  // Voorstellen per bijlage-D-veld (geel, overschrijfbaar, ververst per veld).
  functie: Nen3140AutofillVeld
  ingangsdatum: Nen3140AutofillVeld
  einddatum: Nen3140AutofillVeld
  aanwijzingsdatum: Nen3140AutofillVeld
  installaties: Nen3140AutofillVeld
  /** Keuzelijst-opties "Deel van de installatie" (rolprofiel-data, geseed). */
  installatieOpties: string[]
  werkzaamheden: string[]
  beperkingen: string[]
  verantwoordelijkheden: Nen3140AutofillVeld
  /** Middelen-voorstel uit de FO-315-registratie (PBM/meetinstrument/koffer). */
  middelen: Nen3140AutofillMiddelRegel[]
  middelenBron: string
  /** true = geen geregistreerde middelen; het document toont dat expliciet. */
  geenMiddelenGeregistreerd: boolean
  plaatsAanwijzing: Nen3140AutofillVeld
  aangewezenDoorNaam: string | null
  aangewezenDoorFunctie: Nen3140AutofillVeld
  // Certificaat-bron (voorstel + keuze-opties van deze medewerker).
  certificaatVoorstelId: string | null
  certificaatOpties: Nen3140CertificaatOptieDTO[]
  /** Waarschuwingen, bv. einddatum voorbij de certificaat-geldigheid. */
  waarschuwingen: string[]
  rolprofielId: string | null
  rolprofielNaam: string | null
  // ── batch-58 nen3140-v2 (FO-431) ────────────────────────────────────────
  /** BV waarnamens wordt aangewezen (voorstel uit het dienstverband, §A3). */
  aangewezenNamensEntityId: Nen3140AutofillVeld
  aangewezenNamensEntityNaam: string | null
  /** De normversie die vandaag geldt; wordt op de aanwijzing bevroren (T8). */
  normversie: string
  /** Voorstellen voor de gestructureerde onderbouwing (stap 2). */
  nenTrainingNaam: Nen3140AutofillVeld
  nenTrainingDatum: Nen3140AutofillVeld
  /** Voorstel voor de bevoegdhedenmatrix (stap 4), uit het rolprofiel. */
  matrixVoorstel: Nen3140MatrixRegel[]
}

/** Klant-DTO (allowlist): NOOIT interne notities/audit/HR-data. */
export interface KlantNen3140AanwijzingDTO {
  delingId: string
  medewerkerNaam: string
  niveau: Nen3140Niveau
  niveauLabel: string
  ingangsdatum: string | null
  einddatum: string | null
  badge: Nen3140KlantBadge
  badgeLabel: string
  documentDownloadUrl: string | null
}

// ─── Zod input-schema's ──────────────────────────────────────────────────────

export const CreateNen3140AanwijzingSchema = z.object({
  employeeId: z.string().min(1),
  niveau: Nen3140NiveauEnum,
  rolprofielId: z.string().min(1).nullable().optional(),
  aanwijzingsdatum: z.string().datetime().nullable().optional(),
  ingangsdatum: z.string().datetime().nullable().optional(),
  einddatum: z.string().datetime().nullable().optional(),
  werkzaamheden: z.array(z.string()).optional().default([]),
  beperkingen: z.array(z.string()).optional().default([]),
  omstandigheden: z.string().nullable().optional(),
  installaties: z.string().nullable().optional(),
  spanningsgebieden: z.string().nullable().optional(),
  voorwaarden: z.string().nullable().optional(),
  // FO-334 (batch-39) — bijlage-D-velden (read <-> write consistent: nullable).
  functie: z.string().nullable().optional(),
  verantwoordelijkheden: z.string().nullable().optional(),
  middelen: z.array(z.string()).optional(),
  plaatsAanwijzing: z.string().nullable().optional(),
  certificaatId: z.string().min(1).nullable().optional(),
  bewijsNotities: z.string().nullable().optional(),
  afwijkingsreden: z.string().nullable().optional(),
  aangewezenDoorId: z.string().min(1).nullable().optional(),
  aangewezenDoorFunctie: z.string().nullable().optional(),
  goedkeurderId: z.string().min(1).nullable().optional(),
  // batch-58 nen3140-v2 (FO-431) — BV waarnamens wordt aangewezen + de
  // gestructureerde onderbouwing (stap 2). Read <-> write consistent: elk veld
  // dat de read-DTO als null teruggeeft is hier `.nullable()`.
  aangewezenNamensEntityId: z.string().min(1).nullable().optional(),
  opleidingNiveau: z.string().trim().max(120).nullable().optional(),
  opleidingRichting: z.string().trim().max(200).nullable().optional(),
  nenTrainingNaam: z.string().trim().max(200).nullable().optional(),
  nenTrainingDatum: z.string().datetime().nullable().optional(),
  jarenErvaring: z.number().int().min(0).max(60).nullable().optional(),
  ervaringInstallaties: z.string().trim().max(4000).nullable().optional(),
  ervaringWerkzaamheden: z.string().trim().max(4000).nullable().optional(),
  wizardStap: z.number().int().min(1).max(4).optional(),
})
export type CreateNen3140AanwijzingInput = z.infer<typeof CreateNen3140AanwijzingSchema>

// LET OP (FO-334-fix): `.partial()` op een veld mét `.default([])` laat die
// default alsnog vuren — een PATCH zonder `werkzaamheden`/`beperkingen` zou de
// lijsten dan stil naar leeg wissen. Voor de update-variant vervangen we de
// lijst-velden daarom door default-loze optionals: afwezig = niet wijzigen.
export const UpdateNen3140AanwijzingSchema = partialZonderDefaults(
  CreateNen3140AanwijzingSchema.omit({
    employeeId: true,
    werkzaamheden: true,
    beperkingen: true,
    middelen: true,
  }),
)
  .extend({
    werkzaamheden: z.array(z.string()).optional(),
    beperkingen: z.array(z.string()).optional(),
    middelen: z.array(z.string()).optional(),
  })
export type UpdateNen3140AanwijzingInput = z.infer<typeof UpdateNen3140AanwijzingSchema>

/** Statusovergang (activeren/intrekken/controle/etc.). */
export const Nen3140StatusActieSchema = z.object({
  naar: z.enum(NEN3140_STATUSSEN),
  reden: z.string().trim().max(1000).nullable().optional(),
  scanHandtekeningPath: z.string().nullable().optional(),
})
export type Nen3140StatusActieInput = z.infer<typeof Nen3140StatusActieSchema>

export const Nen3140TekenSchema = z.object({
  signatureData: z.string().min(50).max(500_000),
})
export type Nen3140TekenInput = z.infer<typeof Nen3140TekenSchema>

export const Nen3140WerkgeverTekenSchema = z.object({
  naam: z.string().trim().min(2).max(120),
})
export type Nen3140WerkgeverTekenInput = z.infer<typeof Nen3140WerkgeverTekenSchema>

export const Nen3140DeelSchema = z
  .object({
    clientId: z.string().min(1),
    grondslag: Nen3140DeelGrondslagEnum,
    grondslagToelichting: z.string().trim().max(1000).nullable().optional(),
    periodeVan: z.string().datetime().nullable().optional(),
    periodeTot: z.string().datetime().nullable().optional(),
    projectId: z.string().min(1).nullable().optional(),
    werkbonId: z.string().min(1).nullable().optional(),
  })
  .refine(
    (v) => v.grondslag !== 'CONTRACTUELE_NOODZAAK' || (v.grondslagToelichting?.trim().length ?? 0) >= 10,
    { message: 'Bij contractuele noodzaak is een toelichting (min. 10 tekens) verplicht', path: ['grondslagToelichting'] },
  )
export type Nen3140DeelInput = z.infer<typeof Nen3140DeelSchema>

export const Nen3140VereisteSchema = z
  .object({
    niveau: Nen3140NiveauEnum,
    verplicht: z.boolean().optional().default(true),
    actie: Nen3140VereisteActieEnum.optional().default('WAARSCHUWEN'),
    clientId: z.string().min(1).nullable().optional(),
    werkbonId: z.string().min(1).nullable().optional(),
    projectId: z.string().min(1).nullable().optional(),
    // batch-58 nen3140-v2 (FO-431) — derde laag aan/uit per vereiste.
    klantAutorisatieVereist: z.boolean().optional().default(false),
  })
  .refine(
    (v) => [v.clientId, v.werkbonId, v.projectId].filter(Boolean).length === 1,
    { message: 'Kies precies één doel (relatie, werkbon of project)', path: ['clientId'] },
  )
  .refine((v) => !v.klantAutorisatieVereist || Boolean(v.clientId), {
    message: 'Een klantautorisatie hoort bij een relatie, niet bij een losse werkbon of project',
    path: ['klantAutorisatieVereist'],
  })
export type Nen3140VereisteInput = z.infer<typeof Nen3140VereisteSchema>

/**
 * Bijwerken van een bestaande vereiste. DIAGNOSE batch-58 (D6): de PATCH-route
 * las het body-object rauw uit en castte `actie` naar de enum zonder validatie -
 * een typefout landde daardoor als ongeldige enum-waarde in de database.
 */
export const UpdateNen3140VereisteSchema = z.object({
  verplicht: z.boolean().optional(),
  actie: Nen3140VereisteActieEnum.optional(),
  klantAutorisatieVereist: z.boolean().optional(),
})
export type UpdateNen3140VereisteInput = z.infer<typeof UpdateNen3140VereisteSchema>

export const Nen3140RolprofielSchema = z.object({
  niveau: Nen3140NiveauEnum,
  naam: z.string().trim().min(2).max(120),
  omschrijving: z.string().nullable().optional(),
  toegestaneWerkzaamheden: z.array(z.string()).optional().default([]),
  nietToegestaneWerkzaamheden: z.array(z.string()).optional().default([]),
  standaardBeperkingen: z.array(z.string()).optional().default([]),
  vereisteCertificaatTypes: z.array(z.string()).optional().default([]),
  vereisteSkills: z.array(z.string()).optional().default([]),
  standaardGeldigheidsBron: Nen3140GeldigheidsBronEnum.optional().default('GELIJK_AAN_CERTIFICAAT'),
  maxGeldigheidMaanden: z.number().int().min(1).max(120).nullable().optional(),
  vereisteGoedkeurderRol: z.string().nullable().optional(),
  documentTemplateId: z.string().min(1).nullable().optional(),
  // FO-334 (batch-39) — installatie-keuzelijst (data, beheerbaar).
  standaardInstallatie: z.string().nullable().optional(),
  installatieOpties: z.array(z.string()).optional(),
  combinatieApart: z.boolean().optional().default(true),
  actief: z.boolean().optional().default(true),
  isConcept: z.boolean().optional().default(true),
  volgorde: z.number().int().min(0).optional().default(0),
})
export type Nen3140RolprofielInput = z.infer<typeof Nen3140RolprofielSchema>

export const Nen3140StamlijstSchema = z.object({
  naam: z.string().trim().min(2).max(160),
  categorie: z.string().trim().max(80).nullable().optional(),
  actief: z.boolean().optional().default(true),
  volgorde: z.number().int().min(0).optional().default(0),
})
export type Nen3140StamlijstInput = z.infer<typeof Nen3140StamlijstSchema>

/** Automatisch-delen-bij-inzet per klant (default uit). */
export const Nen3140AutoDeelSchema = z.object({
  clientId: z.string().min(1),
  automatischDelen: z.boolean(),
})
export type Nen3140AutoDeelInput = z.infer<typeof Nen3140AutoDeelSchema>

// ═══════════════════════════════════════════════════════════════════════════
// batch-58 nen3140-v2 (FO-431) — aanwijsbeleid, beoordelaars-register,
// beoordelingscriteria, bevoegdhedenmatrix en klantspecifieke autorisatie
// ═══════════════════════════════════════════════════════════════════════════
//
// Drie lagen (PO-adviesbrief):
//   laag 1  certificaat        - bewijst opleiding (EmployeeCertificate)
//   laag 2  STREEP-aanwijzing  - ons aanwijzingsbesluit (Nen3140Aanwijzing)
//   laag 3  klant-autorisatie  - de IV van de klant laat deze persoon toe
// Een hogere laag kan nooit zonder de laag eronder; verlopen gedraagt zich als
// ontbrekend. Alles hieronder is gedeeld tussen API en frontend: de API parst
// met dezelfde schema's als waarmee het formulier client-side valideert.

/** Default-normversie zolang het beleid nog niet is vastgesteld (§A1 / A4). */
export const NEN3140_NORMVERSIE_DEFAULT = 'NEN 3140:2011+A3:2019'

// ─── Beoordelaars-register ───────────────────────────────────────────────────

export const Nen3140BeoordelaarGrondslagEnum = z.enum([
  'EIGEN_AANWIJZING',
  'WERKGEVERS_DELEGATIE',
])
export type Nen3140BeoordelaarGrondslag = z.infer<typeof Nen3140BeoordelaarGrondslagEnum>

export const NEN3140_BEOORDELAAR_GRONDSLAG_LABELS: Record<Nen3140BeoordelaarGrondslag, string> = {
  EIGEN_AANWIJZING: 'Eigen actieve WV/IV-aanwijzing',
  WERKGEVERS_DELEGATIE: 'Werkgevers-delegatie (met motivering)',
}

export interface Nen3140BeoordelaarDTO {
  id: string
  entityId: string
  entityNaam: string
  userId: string
  gebruikerNaam: string
  gebruikerEmail: string | null
  grondslag: Nen3140BeoordelaarGrondslag
  motivering: string | null
  aanwijzingId: string | null
  /** Niveau van de onderliggende eigen aanwijzing (informatief, null = geen). */
  eigenAanwijzingNiveau: Nen3140Niveau | null
  magBeoordelen: boolean
  magOndertekenen: boolean
  geldigVan: string | null
  geldigTot: string | null
  actief: boolean
  /** Afgeleid: is deze registratie vandaag bruikbaar (actief + binnen periode)? */
  geldigNu: boolean
  createdAt: string
}

/** Is de registratie op `peil` bruikbaar? Puur - gedeeld door API en UI. */
export function beoordelaarGeldigOp(
  reg: { actief: boolean; geldigVan: string | Date | null; geldigTot: string | Date | null },
  peil: Date,
): boolean {
  if (!reg.actief) return false
  const van = reg.geldigVan ? new Date(reg.geldigVan) : null
  const tot = reg.geldigTot ? new Date(reg.geldigTot) : null
  if (van && van.getTime() > peil.getTime()) return false
  if (tot && tot.getTime() < peil.getTime()) return false
  return true
}

export const Nen3140BeoordelaarSchema = z
  .object({
    entityId: z.string().min(1),
    userId: z.string().min(1),
    grondslag: Nen3140BeoordelaarGrondslagEnum,
    motivering: z.string().trim().max(2000).nullable().optional(),
    aanwijzingId: z.string().min(1).nullable().optional(),
    magBeoordelen: z.boolean().optional().default(true),
    magOndertekenen: z.boolean().optional().default(true),
    geldigVan: z.string().datetime().nullable().optional(),
    geldigTot: z.string().datetime().nullable().optional(),
    actief: z.boolean().optional().default(true),
  })
  .refine((v) => v.grondslag !== 'WERKGEVERS_DELEGATIE' || (v.motivering?.trim().length ?? 0) >= 10, {
    message: 'Bij een werkgevers-delegatie is een motivering (min. 10 tekens) verplicht',
    path: ['motivering'],
  })
export type Nen3140BeoordelaarInput = z.infer<typeof Nen3140BeoordelaarSchema>

export const UpdateNen3140BeoordelaarSchema = z.object({
  grondslag: Nen3140BeoordelaarGrondslagEnum.optional(),
  motivering: z.string().trim().max(2000).nullable().optional(),
  aanwijzingId: z.string().min(1).nullable().optional(),
  magBeoordelen: z.boolean().optional(),
  magOndertekenen: z.boolean().optional(),
  geldigVan: z.string().datetime().nullable().optional(),
  geldigTot: z.string().datetime().nullable().optional(),
  actief: z.boolean().optional(),
})
export type UpdateNen3140BeoordelaarInput = z.infer<typeof UpdateNen3140BeoordelaarSchema>

// ─── Aanwijsbeleid (normversie + rol-definities + beleidsdocument) ───────────

export interface Nen3140BeleidDTO {
  normversie: string
  normversieIngangsdatum: string | null
  vorigeNormversie: string | null
  /** De versie die VANDAAG geldt (kan de vorige zijn als de nieuwe later ingaat). */
  geldendeNormversie: string
  rolDefinitiesTekst: string | null
  beleidTekst: string | null
  beleidDocumentId: string | null
  updatedAt: string | null
}

export const Nen3140BeleidSchema = z.object({
  normversie: z.string().trim().min(3).max(120),
  normversieIngangsdatum: z.string().datetime().nullable().optional(),
  rolDefinitiesTekst: z.string().max(20_000).nullable().optional(),
  beleidTekst: z.string().max(50_000).nullable().optional(),
  beleidDocumentId: z.string().min(1).nullable().optional(),
})
export type Nen3140BeleidInput = z.infer<typeof Nen3140BeleidSchema>

/**
 * Welke normversie geldt op `peil`? Een nieuwe versie met een ingangsdatum in de
 * toekomst geldt nog niet; tot die datum blijft de vorige versie staan. Zo kan
 * de 2026-uitgave alvast worden vastgelegd zonder bestaande stukken te raken
 * (T8: nieuwe aanwijzingen dragen de nieuwe versie, oude blijven ongewijzigd).
 */
export function geldendeNormversie(
  beleid: {
    normversie: string
    normversieIngangsdatum: string | Date | null
    vorigeNormversie: string | null
  } | null,
  peil: Date,
): string {
  if (!beleid) return NEN3140_NORMVERSIE_DEFAULT
  const ingang = beleid.normversieIngangsdatum ? new Date(beleid.normversieIngangsdatum) : null
  if (ingang && ingang.getTime() > peil.getTime()) {
    return beleid.vorigeNormversie ?? NEN3140_NORMVERSIE_DEFAULT
  }
  return beleid.normversie
}

// ─── Beoordelingscriteria (stap 3) ──────────────────────────────────────────

export const Nen3140CriteriumAntwoordEnum = z.enum(['JA', 'NEE', 'NVT'])
export type Nen3140CriteriumAntwoord = z.infer<typeof Nen3140CriteriumAntwoordEnum>

export const NEN3140_CRITERIUM_ANTWOORD_LABELS: Record<Nen3140CriteriumAntwoord, string> = {
  JA: 'Ja',
  NEE: 'Nee',
  NVT: 'Niet van toepassing',
}

export interface Nen3140CriteriumDTO {
  id: string
  code: string
  vraag: string
  toelichting: string | null
  niveaus: Nen3140Niveau[]
  blokkerend: boolean
  actief: boolean
  volgorde: number
}

export const Nen3140CriteriumSchema = z.object({
  code: z
    .string()
    .trim()
    .min(2)
    .max(60)
    .regex(/^[A-Z0-9_]+$/, 'Gebruik alleen hoofdletters, cijfers en underscores'),
  vraag: z.string().trim().min(5).max(1000),
  toelichting: z.string().trim().max(2000).nullable().optional(),
  niveaus: z.array(Nen3140NiveauEnum).optional().default([]),
  blokkerend: z.boolean().optional().default(true),
  actief: z.boolean().optional().default(true),
  volgorde: z.number().int().min(0).max(999).optional().default(0),
})
export type Nen3140CriteriumInput = z.infer<typeof Nen3140CriteriumSchema>

export const UpdateNen3140CriteriumSchema = partialZonderDefaults(Nen3140CriteriumSchema.omit({ code: true }))
export type UpdateNen3140CriteriumInput = z.infer<typeof UpdateNen3140CriteriumSchema>

/**
 * Eén bevroren antwoord op de aanwijzing. De vraagtekst wordt MEE bevroren:
 * verandert de configuratie later, dan blijft leesbaar waarop destijds "ja" of
 * "nee" is geantwoord (§5.3 / T3).
 */
export const Nen3140CriteriumAntwoordRegelSchema = z.object({
  code: z.string().trim().min(1).max(60),
  vraag: z.string().trim().min(1).max(1000),
  antwoord: Nen3140CriteriumAntwoordEnum,
  blokkerend: z.boolean(),
  toelichting: z.string().trim().max(2000).nullable().optional(),
})
export type Nen3140CriteriumAntwoordRegel = z.infer<typeof Nen3140CriteriumAntwoordRegelSchema>

export const Nen3140BeoordelingSchema = z.object({
  antwoorden: z.array(Nen3140CriteriumAntwoordRegelSchema).min(1),
  /** Gemotiveerde afwijking; verplicht zodra een blokkerend criterium "nee" is. */
  afwijkingMotivering: z.string().trim().max(4000).nullable().optional(),
})
export type Nen3140BeoordelingInput = z.infer<typeof Nen3140BeoordelingSchema>

export interface Nen3140BeoordelingUitkomst {
  /** Mag de aanwijzing op grond van de beoordeling geactiveerd worden? */
  ok: boolean
  /** Codes van blokkerende criteria die met "nee" zijn beantwoord. */
  blokkerendeNees: string[]
  /** true = er is een "nee" zonder gemotiveerde afwijking. */
  mistMotivering: boolean
  /** Codes van criteria die (nog) niet beantwoord zijn. */
  onbeantwoord: string[]
}

/**
 * Puur: mag deze beoordeling door? Elk "nee" op een blokkerend criterium
 * blokkeert de activatie, tenzij er een gemotiveerde afwijking bij staat (§B3 /
 * T2). Niet-blokkerende criteria met "nee" worden vastgelegd maar houden niets
 * tegen. Ontbrekende antwoorden blokkeren altijd (geen stilzwijgend "ja").
 *
 * BELANGRIJK - wie bepaalt "blokkerend"? De CONFIGURATIE, zolang het criterium
 * nog bestaat. Het bevroren antwoord draagt zijn eigen vlag mee (voor de
 * leesbaarheid van het dossier), maar die vlag mag de veiligheidsklem niet
 * sturen: wordt een criterium later blokkerend gemaakt, dan moet een nog niet
 * geactiveerde aanwijzing met een ongemotiveerde "nee" daarop alsnog
 * tegengehouden worden. Voor codes die uit de configuratie zijn verdwenen valt
 * hij terug op de bevroren vlag - dat is de enige bron die er dan nog is.
 * Bevroren (ACTIEVE) aanwijzingen worden nooit opnieuw langs deze functie
 * gehaald, dus §5.3/T3 blijft gelden.
 */
export function beoordeelCriteria(
  criteria: Pick<Nen3140CriteriumDTO, 'code' | 'blokkerend'>[],
  antwoorden: Nen3140CriteriumAntwoordRegel[],
  afwijkingMotivering: string | null | undefined,
): Nen3140BeoordelingUitkomst {
  const perCode = new Map(antwoorden.map((a) => [a.code, a]))
  const configBlokkerend = new Map(criteria.map((c) => [c.code, c.blokkerend]))
  const onbeantwoord = criteria.filter((c) => !perCode.has(c.code)).map((c) => c.code)
  const blokkerendeNees = antwoorden
    .filter((a) => (configBlokkerend.get(a.code) ?? a.blokkerend) && a.antwoord === 'NEE')
    .map((a) => a.code)
  const mistMotivering = blokkerendeNees.length > 0 && (afwijkingMotivering?.trim().length ?? 0) < 10
  return {
    ok: onbeantwoord.length === 0 && !mistMotivering,
    blokkerendeNees,
    mistMotivering,
    onbeantwoord,
  }
}

/** Geldt dit criterium voor dit niveau? Lege niveau-lijst = alle niveaus. */
export function criteriumGeldtVoor(
  criterium: Pick<Nen3140CriteriumDTO, 'niveaus'>,
  niveau: Nen3140Niveau,
): boolean {
  return criterium.niveaus.length === 0 || criterium.niveaus.includes(niveau)
}

// ─── Bevoegdhedenmatrix (stap 4) ────────────────────────────────────────────

export const Nen3140MatrixRegelSchema = z.object({
  naam: z.string().trim().min(1).max(200),
  soort: z.enum(['WERKZAAMHEID', 'BEPERKING']),
  /** true = toegestaan (vinkje), false = niet toegestaan (kruisje). */
  toegestaan: z.boolean(),
  bron: z.enum(['ROLPROFIEL', 'HANDMATIG']),
  toelichting: z.string().trim().max(500).nullable().optional(),
})
export type Nen3140MatrixRegel = z.infer<typeof Nen3140MatrixRegelSchema>

export const Nen3140MatrixSchema = z.object({
  regels: z.array(Nen3140MatrixRegelSchema),
  werkenOnderSpanning: z.boolean(),
  werkenOnderSpanningToelichting: z.string().trim().max(500).nullable().optional(),
  schakelbevoegdheid: z.boolean(),
  schakelbevoegdheidOmschrijving: z.string().trim().max(500).nullable().optional(),
})
export type Nen3140MatrixInput = z.infer<typeof Nen3140MatrixSchema>

/**
 * Puur: bouw de matrix-voorstelregels uit een rolprofiel. Toegestane
 * werkzaamheden krijgen een vinkje, expliciet niet-toegestane een kruisje;
 * standaardbeperkingen komen als beperking-regels mee. De opsteller mag elke
 * regel per aanwijzing omzetten (dan wordt de bron HANDMATIG).
 */
export function matrixVoorstelUitRolprofiel(
  rolprofiel: {
    toegestaneWerkzaamheden: string[]
    nietToegestaneWerkzaamheden: string[]
    standaardBeperkingen: string[]
  } | null,
): Nen3140MatrixRegel[] {
  if (!rolprofiel) return []
  const regels: Nen3140MatrixRegel[] = []
  for (const naam of rolprofiel.toegestaneWerkzaamheden) {
    regels.push({ naam, soort: 'WERKZAAMHEID', toegestaan: true, bron: 'ROLPROFIEL' })
  }
  for (const naam of rolprofiel.nietToegestaneWerkzaamheden) {
    if (regels.some((r) => r.naam === naam)) continue
    regels.push({ naam, soort: 'WERKZAAMHEID', toegestaan: false, bron: 'ROLPROFIEL' })
  }
  for (const naam of rolprofiel.standaardBeperkingen) {
    regels.push({ naam, soort: 'BEPERKING', toegestaan: false, bron: 'ROLPROFIEL' })
  }
  return regels
}

/** Wijkt de matrix af van het rolprofiel-voorstel? (vastleggen + tonen, T3). */
export function matrixWijktAf(
  matrix: Nen3140MatrixRegel[],
  voorstel: Nen3140MatrixRegel[],
): boolean {
  if (matrix.some((r) => r.bron === 'HANDMATIG')) return true
  if (matrix.length !== voorstel.length) return true
  const key = (r: Nen3140MatrixRegel): string => `${r.soort}|${r.naam}|${r.toegestaan}`
  const links = new Set(matrix.map(key))
  return voorstel.some((r) => !links.has(key(r)))
}

// ─── Onderbouwing (stap 2) ──────────────────────────────────────────────────

export const Nen3140OnderbouwingSchema = z.object({
  opleidingNiveau: z.string().trim().max(120).nullable().optional(),
  opleidingRichting: z.string().trim().max(200).nullable().optional(),
  nenTrainingNaam: z.string().trim().max(200).nullable().optional(),
  nenTrainingDatum: z.string().datetime().nullable().optional(),
  jarenErvaring: z.number().int().min(0).max(60).nullable().optional(),
  ervaringInstallaties: z.string().trim().max(4000).nullable().optional(),
  ervaringWerkzaamheden: z.string().trim().max(4000).nullable().optional(),
})
export type Nen3140OnderbouwingInput = z.infer<typeof Nen3140OnderbouwingSchema>

// ─── Klantspecifieke autorisatie (laag 3) ───────────────────────────────────

export const Nen3140KlantAutorisatieStatusEnum = z.enum([
  'AANGEVRAAGD',
  'ACTIEF',
  'GEWEIGERD',
  'VERLOPEN',
  'INGETROKKEN',
])
export type Nen3140KlantAutorisatieStatus = z.infer<typeof Nen3140KlantAutorisatieStatusEnum>

export const NEN3140_KLANT_AUTORISATIE_STATUS_LABELS: Record<
  Nen3140KlantAutorisatieStatus,
  string
> = {
  AANGEVRAAGD: 'Aangevraagd',
  ACTIEF: 'Actief',
  GEWEIGERD: 'Geweigerd',
  VERLOPEN: 'Verlopen',
  INGETROKKEN: 'Ingetrokken',
}

/** Weergave-status incl. de afgeleide "verloopt binnenkort" (nooit opgeslagen). */
export type Nen3140KlantAutorisatieWeergaveStatus =
  | Nen3140KlantAutorisatieStatus
  | 'VERLOOPT_BINNENKORT'

export const NEN3140_KLANT_AUTORISATIE_WEERGAVE_LABELS: Record<
  Nen3140KlantAutorisatieWeergaveStatus,
  string
> = {
  ...NEN3140_KLANT_AUTORISATIE_STATUS_LABELS,
  VERLOOPT_BINNENKORT: 'Verloopt binnenkort',
}

/** Afgeleide weergave-status uit opgeslagen status + einddatum (zoals laag 2). */
export function bepaalKlantAutorisatieWeergaveStatus(
  status: Nen3140KlantAutorisatieStatus,
  einddatum: string | Date | null,
  now: Date,
  waarschuwingsDagen = 90,
): Nen3140KlantAutorisatieWeergaveStatus {
  if (status !== 'ACTIEF') return status
  if (!einddatum) return 'ACTIEF'
  const eind = typeof einddatum === 'string' ? new Date(einddatum) : einddatum
  const dagen = Math.floor((eind.getTime() - now.getTime()) / 86_400_000)
  if (dagen < 0) return 'VERLOPEN'
  if (dagen <= waarschuwingsDagen) return 'VERLOOPT_BINNENKORT'
  return 'ACTIEF'
}

/**
 * Puur: dekt deze autorisatie de peildatum? Alleen ACTIEF binnen de periode
 * telt - een verlopen of ingetrokken autorisatie gedraagt zich als ontbrekend
 * (§D10).
 */
export function klantAutorisatieDekt(
  a: {
    status: Nen3140KlantAutorisatieStatus
    ingangsdatum: string | Date | null
    einddatum: string | Date | null
  },
  peil: Date,
): boolean {
  if (a.status !== 'ACTIEF') return false
  const van = a.ingangsdatum ? new Date(a.ingangsdatum) : null
  const tot = a.einddatum ? new Date(a.einddatum) : null
  if (van && van.getTime() > peil.getTime()) return false
  // batch-61 nen3140-verificatie (FO-454, V8): de einddatum-dag zelf dekt niet
  // meer (`<=`). Een datum-only einddatum staat als middernacht UTC opgeslagen;
  // met `<` dekte een peildatum die óók middernacht is (het planbord geeft de
  // dagregel-datum door) die dag nog wél, terwijl de weergave-status en de
  // verloop-cron hem al "verlopen" noemden. Eén grens voor alle lagen.
  if (tot && tot.getTime() <= peil.getTime()) return false
  return true
}

export interface Nen3140KlantAutorisatieDTO {
  id: string
  clientId: string
  clientNaam: string
  employeeId: string
  medewerkerNaam: string
  clientLocationId: string | null
  locatieNaam: string | null
  installatiegebied: string | null
  aanwijzingId: string | null
  aanwijzingNiveau: Nen3140Niveau | null
  aanwijzingStatus: Nen3140Status | null
  status: Nen3140KlantAutorisatieStatus
  weergaveStatus: Nen3140KlantAutorisatieWeergaveStatus
  voorwaarden: string | null
  contactpersoonNaam: string | null
  contactpersoonFunctie: string | null
  contactpersoonEmail: string | null
  ingangsdatum: string | null
  einddatum: string | null
  dagenTotVerloop: number | null
  bewijsDocumentId: string | null
  bewijsToelichting: string | null
  aangevraagdOp: string | null
  besluitOp: string | null
  geweigerdReden: string | null
  ingetrokkenOp: string | null
  ingetrokkenReden: string | null
  createdAt: string
}

export const CreateNen3140KlantAutorisatieSchema = z.object({
  clientId: z.string().min(1),
  employeeId: z.string().min(1),
  clientLocationId: z.string().min(1).nullable().optional(),
  installatiegebied: z.string().trim().max(500).nullable().optional(),
  aanwijzingId: z.string().min(1).nullable().optional(),
  voorwaarden: z.string().trim().max(4000).nullable().optional(),
  contactpersoonNaam: z.string().trim().max(160).nullable().optional(),
  contactpersoonFunctie: z.string().trim().max(160).nullable().optional(),
  contactpersoonEmail: z.union([z.string().trim().email().max(200), z.literal('')]).nullable().optional(),
  ingangsdatum: z.string().datetime().nullable().optional(),
  einddatum: z.string().datetime().nullable().optional(),
  bewijsDocumentId: z.string().min(1).nullable().optional(),
  bewijsToelichting: z.string().trim().max(2000).nullable().optional(),
})
export type CreateNen3140KlantAutorisatieInput = z.infer<typeof CreateNen3140KlantAutorisatieSchema>

export const UpdateNen3140KlantAutorisatieSchema = CreateNen3140KlantAutorisatieSchema.omit({
  clientId: true,
  employeeId: true,
}).partial()
export type UpdateNen3140KlantAutorisatieInput = z.infer<typeof UpdateNen3140KlantAutorisatieSchema>

/** Statusbesluit op een klant-autorisatie (activeren/weigeren/intrekken). */
export const Nen3140KlantAutorisatieBesluitSchema = z
  .object({
    naar: z.enum(['ACTIEF', 'GEWEIGERD', 'INGETROKKEN', 'AANGEVRAAGD']),
    reden: z.string().trim().max(2000).nullable().optional(),
  })
  .refine((v) => (v.naar !== 'GEWEIGERD' && v.naar !== 'INGETROKKEN') || Boolean(v.reden?.trim()), {
    message: 'Weigeren en intrekken vereisen een reden',
    path: ['reden'],
  })
export type Nen3140KlantAutorisatieBesluitInput = z.infer<
  typeof Nen3140KlantAutorisatieBesluitSchema
>

// ─── Drie-lagen-status (planning + overzicht, gedeelde resolver) ────────────

export type Nen3140Laag = 'CERTIFICAAT' | 'AANWIJZING' | 'KLANT_AUTORISATIE'

export type Nen3140FindingReden =
  | 'GEEN'
  | 'VERLOPEN'
  | 'NOG_NIET_INGEGAAN'
  | 'LAGER_NIVEAU'
  // batch-61 nen3140-verificatie (FO-454, V5): de screening ziet nu dezelfde
  // rijen als het overzicht, dus ook een aanwijzing die nog in behandeling is,
  // ingetrokken is, of vervangen/gearchiveerd - met de bijbehorende uitleg i.p.v.
  // een kaal "geen aanwijzing".
  | 'IN_BEHANDELING'
  | 'INGETROKKEN'
  | 'NIET_ACTIEF'
  | 'KLANT_AUTORISATIE_ONTBREEKT'
  | 'KLANT_AUTORISATIE_VERLOPEN'
  // batch-61 nen3140-verificatie (FO-454, V2): een actieve autorisatie met een
  // ingangsdatum in de toekomst dekt nog niet, maar is niet "verlopen".
  | 'KLANT_AUTORISATIE_NOG_NIET_INGEGAAN'
  // Idem voor de overige niet-dekkende statussen van laag 3.
  | 'KLANT_AUTORISATIE_AANGEVRAAGD'
  | 'KLANT_AUTORISATIE_GEWEIGERD'
  | 'KLANT_AUTORISATIE_INGETROKKEN'

export const NEN3140_FINDING_REDEN_LABELS: Record<Nen3140FindingReden, string> = {
  GEEN: 'Geen aanwijzing',
  VERLOPEN: 'Aanwijzing verlopen',
  NOG_NIET_INGEGAAN: 'Aanwijzing nog niet ingegaan',
  LAGER_NIVEAU: 'Aanwijzing van een lager niveau',
  IN_BEHANDELING: 'Aanwijzing nog in behandeling (niet ondertekend of geactiveerd)',
  INGETROKKEN: 'Aanwijzing ingetrokken',
  NIET_ACTIEF: 'Aanwijzing niet meer actief (vervangen of gearchiveerd)',
  KLANT_AUTORISATIE_ONTBREEKT: 'NEN 3140-klantautorisatie ontbreekt',
  KLANT_AUTORISATIE_VERLOPEN: 'NEN 3140-klantautorisatie verlopen',
  KLANT_AUTORISATIE_NOG_NIET_INGEGAAN: 'NEN 3140-klantautorisatie nog niet ingegaan',
  KLANT_AUTORISATIE_AANGEVRAAGD: 'NEN 3140-klantautorisatie aangevraagd, nog niet besloten',
  KLANT_AUTORISATIE_GEWEIGERD: 'NEN 3140-klantautorisatie geweigerd door de klant',
  KLANT_AUTORISATIE_INGETROKKEN: 'NEN 3140-klantautorisatie ingetrokken',
}

/** Screening-bevinding zoals de planning-UI hem toont (409-details). */
export interface Nen3140ScreeningFindingDTO {
  employeeId: string
  medewerkerNaam: string
  vereistNiveau: Nen3140Niveau
  heeftNiveau: Nen3140Niveau | null
  reden: Nen3140FindingReden
  laag: Nen3140Laag
  actie: Nen3140VereisteActie
  ernst: 'BLOKKEER' | 'WAARSCHUWING'
  /** Alleen bij laag KLANT_AUTORISATIE gevuld. */
  clientNaam?: string | null
}

/** Eén laag in het medewerker-overzicht (cert / aanwijzing / per klant). */
export interface Nen3140LaagStatusDTO {
  ok: boolean
  label: string
  einddatum: string | null
}

export interface Nen3140KlantLaagDTO {
  clientId: string
  clientNaam: string
  /** Eist deze klant een eigen autorisatie (vlag op de vereiste)? */
  autorisatieVereist: boolean
  status: Nen3140KlantAutorisatieWeergaveStatus | null
  ok: boolean
  einddatum: string | null
}

/** Het drie-lagen-overzicht per medewerker, klant als kolom (§C9). */
export interface Nen3140MedewerkerLagenDTO {
  employeeId: string
  medewerkerNaam: string
  certificaat: Nen3140LaagStatusDTO
  aanwijzing: Nen3140LaagStatusDTO & {
    niveau: Nen3140Niveau | null
    status: Nen3140Status | null
    weergaveStatus: Nen3140WeergaveStatus | null
    aanwijzingId: string | null
  }
  klanten: Nen3140KlantLaagDTO[]
}

/** Volledige drie-lagen-uitkomst voor één (medewerker, klant)-combinatie. */
export interface Nen3140StatusDTO {
  employeeId: string
  clientId: string | null
  certificaatGeldig: boolean
  aanwijzingNiveau: Nen3140Niveau | null
  aanwijzingGeldig: boolean
  klantAutorisatieVereist: boolean
  klantAutorisatieGeldig: boolean
  /** Alle van toepassing zijnde lagen in orde voor de gevraagde klant? */
  volledigInOrde: boolean
}
